import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { useAuth } from "@/context/AuthContext";
import { saveHealthProfile } from "@/lib/profile";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", date_of_birth: "", sex: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await register(form);
    if (res.ok) {
      // Populate the health profile so age/sex-specific bloodwork ranges work
      // from day one (backend reads /settings { age, sex }).
      await saveHealthProfile({ dob: form.date_of_birth, sex: form.sex }).catch(() => {});
      setLoading(false);
      toast.success("Account created");
      navigate("/app/dashboard");
    } else {
      setLoading(false);
      toast.error(res.error);
    }
  };

  return (
    <AuthLayout title="Start free on desktop" subtitle="Create your Better Days account. Same data on mobile."
      footer={<span>Already have an account? <Link to="/login" className="font-semibold text-brand hover:underline">Sign in</Link></span>}>
      <div data-testid="register-form">
        <GoogleSignInButton label="Sign up with Google" />
        <div className="my-4 flex items-center gap-3 text-xs text-muted">
          <span className="h-px flex-1 bg-line" /> or with email <span className="h-px flex-1 bg-line" />
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Full name</label>
          <input value={form.name} onChange={set("name")} required data-testid="register-name"
            className="w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Email</label>
          <input type="email" value={form.email} onChange={set("email")} required data-testid="register-email"
            className="w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink">Password</label>
          <input type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password"
            className="w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Date of birth</label>
            <input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} data-testid="register-dob"
              className="w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink">Sex</label>
            <select value={form.sex} onChange={set("sex")} data-testid="register-sex"
              className="w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-brand">
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>
        <p className="text-xs text-muted">Used to personalise your bloodwork reference ranges. You can change this later in Settings.</p>
        <button type="submit" disabled={loading} data-testid="register-submit"
          className="flex w-full items-center justify-center gap-2 rounded-md bg-brand py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
        </button>
      </form>
    </AuthLayout>
  );
}
